import { useCallback, useMemo } from "react";
import {
  blue,
  cyan,
  geekblue,
  gold,
  green,
  lime,
  magenta,
  orange,
  purple,
  red,
  volcano,
} from "@ant-design/colors";

// Prime multiplier for string hashing (djb2 algorithm variant)
const HASH_MULTIPLIER = 31;

/**
 * Deterministically maps a string to a consistent color from the palette.
 * The same string will always return the same color, making it ideal for
 * user avatars, tags, or labels that need stable color assignment.
 *
 * @param str - Any string (e.g., username, email, ID)
 * @returns A hex color string from the generated palette
 */
export function getConsistentColorForStringAndTokens(str: string = "", colorTokens: string[]) {
  let hash = 0;

  for (let i = 0; i < str.length; i++) {
    hash = str.charCodeAt(i) + hash * HASH_MULTIPLIER;
  }

  const index = Math.abs(hash) % colorTokens.length;

  return colorTokens[index];
}

export function getColorTokens(count: number) {
  {
    // Use Ant Design's preset color palettes for diverse hues
    const baseColors = [
      blue,
      cyan,
      green,
      lime,
      gold,
      orange,
      red,
      volcano,
      magenta,
      purple,
      geekblue,
    ];

    // Use darker shades (indices 5-8) for better contrast with white text
    const darkerShades = [5, 6, 7, 8];
    const colors: string[] = [];

    for (let i = 0; i < count; i++) {
      const paletteIndex = i % baseColors.length;
      const shadeIndex = darkerShades[Math.floor(i / baseColors.length) % darkerShades.length];

      colors.push(baseColors[paletteIndex][shadeIndex]);
    }

    return colors;
  }
}

/**
 * Generates a diverse palette of colors with varied hues for visual distinction.
 * Useful for avatar backgrounds, tags, or any UI element requiring consistent,
 * user-specific colors with good visual variety.
 *
 * @param count - Number of colors to generate (default: 50)
 * @returns Object containing:
 *   - colors: Array of hex color strings with varied hues
 *   - getConsistentColorForString: Function that deterministically maps a string to a color
 *
 */
export const useRandomColorPalette = (count = 50) => {
  const colorTokens = useMemo(() => getColorTokens(count), [count]);

  const getConsistentColorForString = useCallback(
    (str?: string) => getConsistentColorForStringAndTokens(str, colorTokens),
    [colorTokens]
  );

  return { colors: colorTokens, getConsistentColorForString };
};

const fiftyColors = getColorTokens(50);

export function getConsistentColorForString(str: string) {
  return getConsistentColorForStringAndTokens(str, fiftyColors);
}
